import {styleText} from 'node:util'

import {getProjectCliClient, SanityCommand, subdebug} from '@sanity/cli-core'

import {promptForProject} from '../../prompts/promptForProject.js'
import {
  GRAPHQL_API_VERSION,
  type GraphQLEndpoint,
  listGraphQLEndpoints,
} from '../../services/graphql.js'
import {getProjectIdFlag} from '../../util/sharedFlags.js'

const listGraphQLDebug = subdebug('graphql:list')

export class List extends SanityCommand<typeof List> {
  static override description = 'List deployed GraphQL endpoints for the project'
  static override examples = [
    {
      command: '<%= config.bin %> <%= command.id %>',
      description: 'List GraphQL endpoints for the project',
    },
    {
      command: '<%= config.bin %> <%= command.id %> --project-id abc123',
      description: 'List GraphQL endpoints for a specific project',
    },
  ]

  static override flags = {
    ...getProjectIdFlag({
      description: 'Project ID to list GraphQL endpoints for',
      semantics: 'override',
    }),
  }

  public async run(): Promise<void> {
    await this.parse(List)

    const projectId = await this.getProjectId({
      fallback: () => promptForProject({}),
    })

    let endpoints: GraphQLEndpoint[] | undefined
    try {
      endpoints = await listGraphQLEndpoints(projectId)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)

      listGraphQLDebug(`Error fetching GraphQL endpoints for project ${projectId}`, error)
      this.error(`GraphQL endpoints list retrieval failed:\n${message}`, {exit: 1})
    }

    if (!endpoints || endpoints.length === 0) {
      this.log("This project doesn't have any GraphQL endpoints deployed.")
      return
    }

    const client = await getProjectCliClient({
      apiVersion: GRAPHQL_API_VERSION,
      projectId,
    })

    this.log('Here are the GraphQL endpoints deployed for this project:')
    for (const [index, endpoint] of endpoints.entries()) {
      const {dataset, tag} = endpoint
      const url = client.getUrl(`/graphql/${dataset}/${tag}`)

      this.log(`${index + 1}.  ${styleText('bold', 'Dataset:')}     ${dataset}`)
      this.log(`    ${styleText('bold', 'Tag:')}         ${tag}`)
      this.log(`    ${styleText('bold', 'Generation:')}  ${endpoint.generation}`)
      this.log(`    ${styleText('bold', 'Playground:')}  ${endpoint.playgroundEnabled}`)
      this.log(`    ${styleText('bold', 'URL:')}  ${url}\n`)
    }
  }
}
